import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectNaceCodes } from 'store/slices/dataSets/selectors';
import {
  useGetAgentSessionSupplierDiscoveryQuery,
  useStartAgentSessionReviewMutation,
  useUpdateAgentSessionSupplierDiscoveryMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type IUseAICreatorSupplierRootParams from '../interfaces/IUseAICreatorSupplierRootParams';

const useAICreatorSupplierRoot = ({ session }: IUseAICreatorSupplierRootParams) => {
  const { valueChainMapId } = useParams();
  const [updateAgentSessionSupplierDiscovery] = useUpdateAgentSessionSupplierDiscoveryMutation();
  const [
    startAgentSessionReview,
    { isLoading: isStartAgentSessionReviewLoading },
  ] = useStartAgentSessionReviewMutation();

  const { data: naceCodesData } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const {
    data: agentSessionSupplierDiscovery,
    isFetching: isAgentSessionSupplierDiscoveryFetching,
    refetch: refetchAgentSessionSupplierDiscovery,
    fulfilledTimeStamp: agentSessionSupplierDiscoveryFulfilledTimeStamp,
  } = useGetAgentSessionSupplierDiscoveryQuery({
    sessionId: session?.id || 0,
    valueChainMapId: valueChainMapId || '',
  }, {
    skip: !session?.id || !valueChainMapId,
  });

  const handleRefetchAgentSessionSupplierDiscovery = useCallback(() => {
    if (!session?.id || !valueChainMapId) {
      return undefined;
    }

    return refetchAgentSessionSupplierDiscovery();
  }, [refetchAgentSessionSupplierDiscovery, session?.id, valueChainMapId]);

  const agentSessionSupplierDiscoveryUnits = useMemo(
    () => agentSessionSupplierDiscovery?.data ?? [],
    [agentSessionSupplierDiscovery?.data],
  );

  const sessionUnitStatusByUnitId = useMemo(
    () => new Map(
      agentSessionSupplierDiscoveryUnits.map((unit) => [unit.unitId, unit.status] as const),
    ),
    [agentSessionSupplierDiscoveryUnits],
  );

  return {
    valueChainMapId,
    naceCodesData,
    isAgentSessionSupplierDiscoveryFetching,
    refetchAgentSessionSupplierDiscovery: handleRefetchAgentSessionSupplierDiscovery,
    agentSessionSupplierDiscoveryFulfilledTimeStamp,
    sessionUnitStatusByUnitId,
    updateAgentSessionSupplierDiscovery,
    startAgentSessionReview,
    isStartAgentSessionReviewLoading,
  };
};

export default useAICreatorSupplierRoot;
